/**
 * Accessible client-side form validation (progressive enhancement).
 *
 * Browsers' native "Fill out this field" bubbles are not reliably announced by screen readers
 * (VoiceOver on iOS in particular), they leave no aria-invalid on the field, and focus stays where
 * it was. This replaces them, on forms that opt in, with the pattern the theme already uses for
 * server-side errors: aria-invalid on the field plus an inline `.form__field-error` message wired
 * up with aria-describedby, and focus moved to the first invalid field.
 *
 * Opt in by giving a <form> the `data-validate-form` attribute. Everything else is automatic:
 * - `novalidate` is only added once this script runs, so without JS the browser's native
 *   validation still guards the form.
 * - Validation runs in a document-level capture listener, i.e. BEFORE any submit handler on the
 *   form itself (such as <product-form>'s add-to-cart request). An invalid form never reaches
 *   those handlers; a valid one passes through untouched.
 * - Server-rendered errors (Shopify's form.errors markup) are left alone. Only the aria-invalid
 *   and messages this script adds are ever removed by it.
 * - A success message marked `data-form-success` inside an opted-in form is focused on load, so
 *   the post-submit page reload lands a screen reader on the confirmation.
 *
 * Messages come from window.theme.strings.formValidation (locales/en.default.json,
 * accessibility.form_error_*), with English fallbacks.
 *
 * Each failed submit attempt also gets ONE polite live-region announcement (a "N fields need
 * attention" summary) on top of the existing per-field aria-invalid/aria-describedby/focus-first
 * behaviour above — see announceStatus()/STATUS_ATTR below. Per-field errors stay plain <p>
 * elements (no role="alert") specifically so that announcement doesn't double up with N
 * individual interruptions.
 */
(() => {
  if (window.themeFormValidation) return;

  const FORM_SELECTOR = 'form[data-validate-form]';
  const ERROR_ATTR = 'data-form-validation-error';
  const STATUS_ATTR = 'data-form-validation-status';
  const SUCCESS_SELECTOR = '[data-form-success]';

  const FALLBACK_STRINGS = {
    required: 'This field is required.',
    email: 'Enter a valid email address, for example name@example.com.',
    invalid: 'Check this field and try again.',
    summaryOne: '[count] field needs attention.',
    summaryOther: '[count] fields need attention.'
  };

  // field -> { errorEl, addedAriaInvalid }
  const fieldState = new WeakMap();
  // form -> status live-region element
  const statusState = new WeakMap();
  let idCounter = 0;

  const getStrings = () => {
    const themeStrings = window.theme && window.theme.strings && window.theme.strings.formValidation;
    return { ...FALLBACK_STRINGS, ...(themeStrings || {}) };
  };

  /**
   * Returns the message for a field's current validity state.
   * @param {HTMLElement} field - Form control.
   * @returns {string}
   */
  function messageFor(field) {
    const strings = getStrings();
    if (field.validity.valueMissing) return strings.required;
    if (field.validity.typeMismatch && field.type === 'email') return strings.email;
    return strings.invalid;
  }

  /**
   * The element the error message is inserted after. Keeps messages out of combined
   * input-and-button rows, and after a checkbox's label rather than between the two.
   * @param {HTMLElement} field - Form control.
   * @returns {HTMLElement}
   */
  function anchorFor(field) {
    const group = field.closest('.input-with-button, .login-password');
    if (group) return group;

    if ((field.type === 'checkbox' || field.type === 'radio') && field.id) {
      const next = field.nextElementSibling;
      if (next && next.tagName === 'LABEL' && next.htmlFor === field.id) return next;
    }

    return field;
  }

  const getDescribedBy = (field) => (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);

  /**
   * Builds the "N fields need attention" summary text for a failed submit attempt.
   * @param {number} count - Number of invalid fields.
   * @returns {string}
   */
  function summaryMessage(count) {
    const strings = getStrings();
    const template = count === 1 ? strings.summaryOne : strings.summaryOther;
    return template.replace('[count]', count);
  }

  /**
   * Finds or lazily creates the form's visually-hidden status live region. Created on demand
   * (like showError()'s errorEl) rather than on every opted-in form up front, since most forms
   * are never submitted invalid.
   * @param {HTMLFormElement} form - Form to get the status region for.
   * @returns {HTMLElement}
   */
  function getStatusEl(form) {
    let statusEl = statusState.get(form);
    if (!statusEl) {
      statusEl = document.createElement('p');
      statusEl.className = 'visually-hidden';
      statusEl.setAttribute('role', 'status');
      statusEl.setAttribute('aria-live', 'polite');
      statusEl.setAttribute('aria-atomic', 'true');
      statusEl.setAttribute(STATUS_ATTR, '');
      form.insertAdjacentElement('afterbegin', statusEl);
      statusState.set(form, statusEl);
    }
    return statusEl;
  }

  /**
   * Announces a form-level status message, clearing the region first and deferring the real text
   * to the next animation frame. aria-live regions don't re-announce identical text, so without
   * this, two failed submits in a row with the same invalid-field count (the common case) would
   * only ever be announced once. Mirrors assets/cart-drawer.js's announceLiveRegionText().
   * @param {HTMLElement} el - The live region element.
   * @param {string} text - The text to announce.
   */
  function announceLiveRegionText(el, text) {
    if (!el) return;

    el.textContent = '';
    requestAnimationFrame(() => {
      el.textContent = text;
    });
  }

  /**
   * Clears the form's status region, e.g. once every field is valid again.
   * @param {HTMLFormElement} form - Form to clear the status region for.
   */
  function clearStatus(form) {
    const statusEl = statusState.get(form);
    if (statusEl) statusEl.textContent = '';
  }

  /**
   * Whether the form still has any invalid field, including ones associated through the `form`
   * attribute (see validateForm()).
   * @param {HTMLFormElement} form - Form to check.
   * @returns {boolean}
   */
  function formHasInvalidFields(form) {
    return Array.from(form.elements).some((field) => field.willValidate && !field.validity.valid);
  }

  function showError(field) {
    let state = fieldState.get(field);
    if (!state) {
      idCounter += 1;
      const errorEl = document.createElement('p');
      errorEl.className = 'form__field-error';
      errorEl.id = `${field.id || 'form-field'}-validation-error-${idCounter}`;
      errorEl.setAttribute(ERROR_ATTR, '');
      anchorFor(field).insertAdjacentElement('afterend', errorEl);

      state = { errorEl, addedAriaInvalid: field.getAttribute('aria-invalid') !== 'true' };
      fieldState.set(field, state);

      field.setAttribute('aria-describedby', [...getDescribedBy(field), errorEl.id].join(' '));
      field.setAttribute('aria-invalid', 'true');
    }

    state.errorEl.textContent = messageFor(field);
  }

  function clearError(field) {
    const state = fieldState.get(field);
    if (!state) return;

    const remaining = getDescribedBy(field).filter((id) => id !== state.errorEl.id);
    if (remaining.length) {
      field.setAttribute('aria-describedby', remaining.join(' '));
    } else {
      field.removeAttribute('aria-describedby');
    }

    if (state.addedAriaInvalid) field.removeAttribute('aria-invalid');
    state.errorEl.remove();
    fieldState.delete(field);
  }

  /**
   * Validates every control that belongs to the form (including ones associated through the
   * `form` attribute, e.g. product custom options), updating each field's error state.
   * @param {HTMLFormElement} form - Form to validate.
   * @returns {HTMLElement[]} Invalid fields, in document order.
   */
  function validateForm(form) {
    const invalid = [];
    Array.from(form.elements).forEach((field) => {
      if (!field.willValidate) return;
      if (field.validity.valid) {
        clearError(field);
      } else {
        showError(field);
        invalid.push(field);
      }
    });
    return invalid;
  }

  function enhance(form) {
    if (!form.hasAttribute('novalidate')) form.setAttribute('novalidate', '');
  }

  function focusSuccessMessage(root) {
    const success = root.querySelector(`${FORM_SELECTOR} ${SUCCESS_SELECTOR}`);
    if (!success) return;
    if (!success.hasAttribute('tabindex')) success.setAttribute('tabindex', '-1');
    success.focus();
  }

  /**
   * Enhances all opted-in forms inside a root (the document, or a re-rendered section).
   * @param {ParentNode} [root=document] - Where to look for forms.
   */
  function init(root = document) {
    root.querySelectorAll(FORM_SELECTOR).forEach(enhance);
    focusSuccessMessage(root);
  }

  function handleSubmit(evt) {
    const form = evt.target;
    if (!form || form.tagName !== 'FORM' || !form.matches(FORM_SELECTOR)) return;
    if (evt.submitter && evt.submitter.formNoValidate) return;

    enhance(form);
    const invalid = validateForm(form);
    if (!invalid.length) {
      clearStatus(form);
      return;
    }

    announceLiveRegionText(getStatusEl(form), summaryMessage(invalid.length));

    evt.preventDefault();
    evt.stopImmediatePropagation();
    invalid[0].focus();
  }

  function handleFieldChange(evt) {
    const field = evt.target;
    const state = fieldState.get(field);
    if (!state) return;

    if (field.validity.valid) {
      clearError(field);
      if (field.form && !formHasInvalidFields(field.form)) clearStatus(field.form);
    } else {
      state.errorEl.textContent = messageFor(field);
    }
  }

  // Forms rendered after load (quick-add drawers, theme editor) are enhanced just before their
  // first submission: a submit button's click runs before the browser's validation step.
  function handleClick(evt) {
    const button = typeof evt.target.closest === 'function' && evt.target.closest('button, input[type="submit"]');
    if (button && button.form && button.form.matches(FORM_SELECTOR)) enhance(button.form);
  }

  document.addEventListener('submit', handleSubmit, true);
  document.addEventListener('click', handleClick, true);
  document.addEventListener('input', handleFieldChange);
  document.addEventListener('change', handleFieldChange);
  document.addEventListener('shopify:section:load', (evt) => init(evt.target));

  window.themeFormValidation = { init, validateForm };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
})();
